import { GridColDef } from '../DataGrid/DataGrid.tsx';
import React from 'react';
import { Lead } from '@upstart/harmony/lib/legos/dealTypes.ts';
import { missingTextPlaceholder } from '~/utils/constants.ts';
import GridCell from '../../molecules/GridCell/GridCell.tsx';
import { generateCarName } from '~/utils/vehicles.ts';

interface CustomerSearchResultRow {
  id: string;
  customer: {
    name: string;
    phone: string;
    email: string;
  };
  salesperson: string;
  vehicle: {
    name: string;
    status?: string | null;
    stock?: string | null;
  };
  lead: Lead;
}

export const skeletonStyles = { width: '100%', height: '23px' };

export interface CustomerNameSlotProps {
  name: string;
  lead: Lead;
}

export interface DealActionsSlotProps {
  dealId: string;
  lead: Lead;
}

const collator = new Intl.Collator();
const compareStrings = (v1: string, v2: string): number =>
  collator.compare(v1, v2);
export const customerComparator = (
  v1: CustomerSearchResultRow['customer'],
  v2: CustomerSearchResultRow['customer'],
): number => compareStrings(v1.name, v2.name);
export const vehicleComparator = (
  v1: CustomerSearchResultRow['vehicle'],
  v2: CustomerSearchResultRow['vehicle'],
): number => compareStrings(v1.name, v2.name);

export const generateColumns = ({
  DealActionsSlot,
  CustomerNameSlot,
}: {
  DealActionsSlot: React.ComponentType<DealActionsSlotProps>;
  CustomerNameSlot: React.ComponentType<CustomerNameSlotProps>;
}): GridColDef<CustomerSearchResultRow>[] => {
  return [
    {
      field: 'customer',
      headerName: 'Customer',
      flex: 1,
      sortComparator: customerComparator,
      renderCell: (params): React.ReactNode => {
        const { name, phone, email } = params.row.customer;
        return (
          <GridCell
            title={<CustomerNameSlot name={name} lead={params.row.lead} />}
            body1={phone}
            body2={email}
          />
        );
      },
    },
    {
      field: 'salesperson',
      headerName: 'Salesperson',
      flex: 1,
      renderCell: (params): React.ReactNode => {
        const { salesperson } = params.row;
        return <GridCell title={salesperson} />;
      },
    },
    {
      field: 'vehicle',
      headerName: 'Vehicle',
      flex: 1,
      sortComparator: vehicleComparator,
      renderCell: (params): React.ReactNode => {
        const { name, status, stock } = params.row.vehicle;
        let body = missingTextPlaceholder;

        if (status && stock) {
          body = `${status} | Stock: ${stock}`;
        } else if (status) {
          body = status;
        } else if (stock) {
          body = `Stock: ${stock}`;
        }

        return <GridCell title={name} body1={body} />;
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      disableHoverPopOver: true,
      flex: 1,
      renderCell: (params): React.ReactNode => {
        const dealId = params.row.id;
        return <DealActionsSlot dealId={dealId} lead={params.row.lead} />;
      },
    },
  ];
};

export const generateRows = ({
  deals,
}: {
  deals: Lead[];
}): CustomerSearchResultRow[] => {
  return deals.map((deal) => {
    // Leads should have either an `id` or `visitID`, Math.random is only being used
    // as a safe fallback. Calls to this are memoized, but peformance can still suffer
    // in situations like filtering
    const rowId =
      deal.id ||
      (deal.visitID != null ? String(deal.visitID) : String(Math.random()));

    return {
      id: rowId,
      customer: {
        name:
          `${deal.firstName ?? ''} ${deal.lastName ?? ''}`.trim() ||
          missingTextPlaceholder,
        phone: deal.phone || missingTextPlaceholder,
        email: deal.email || missingTextPlaceholder,
      },
      salesperson: deal.salesPerson || missingTextPlaceholder,
      vehicle: {
        name:
          generateCarName({
            year: deal.car?.year,
            make: deal.car?.make,
            model: deal.car?.model,
            trim: deal.car?.trim,
          }) || missingTextPlaceholder,
        status: deal.car?.status,
        stock: deal.car?.stock,
      },
      lead: deal,
    };
  });
};
